import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage, useAuth, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { 
  CheckCircle2, XCircle, Loader2, Mail, Phone, 
  User, Building2, ArrowRight, RefreshCw
} from 'lucide-react';

// Payment Success Page
export const PaymentSuccessPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // Initialize status based on sessionId presence
  const [status, setStatus] = useState(() => sessionId ? 'checking' : 'error');
  const [contact, setContact] = useState(null);
  const [error, setError] = useState(() => sessionId ? '' : 'No session ID provided');
  const [attempts, setAttempts] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    
    const maxAttempts = 10;
    const pollInterval = 1000;
    
    const pollPaymentStatus = async (attempt = 0) => {
      if (attempt >= maxAttempts) {
        setStatus('timeout');
        setError('Payment verification taking longer than expected. Your payment may still be processing.');
        return;
      }

      setAttempts(attempt + 1);

      try {
        const res = await api.get(`/stripe/checkout/status/${sessionId}`);
        
        if (res.data.payment_status === 'paid' && res.data.unlocked) {
          setStatus('success');
          if (res.data.contact) {
            setContact(res.data.contact);
          } else if (res.data.target_user_id) {
            try {
              const contactRes = await api.get(`/stripe/contact/${res.data.target_user_id}`);
              if (contactRes.data.contact) {
                setContact(contactRes.data.contact);
              }
            } catch (e) {
              console.error('Error fetching contact:', e);
            }
          }
        } else if (res.data.status === 'expired') {
          setStatus('error');
          setError('Payment session expired. Please try again.');
        } else {
          pollRef.current = setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
        }
      } catch (err) {
        console.error('Error checking status:', err);
        pollRef.current = setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
      }
    };

    pollPaymentStatus();

    return () => {
      if (pollRef.current) {
        clearTimeout(pollRef.current);
      }
    };
  }, [sessionId]);
  
  const retryPolling = () => {
    window.location.reload();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Please log in</h2>
            <p className="text-muted-foreground mb-4">You need to be logged in to view payment status.</p>
            <Link to="/login">
              <Button>{t('auth.login')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="payment-success-card">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-12 w-12 text-brand mx-auto mb-4 animate-spin" />
              <CardTitle>{t('payments.verifying')}</CardTitle>
              <CardDescription>
                {t('payments.pleaseWait') || `Checking payment status... (Attempt ${attempts}/5)`}
              </CardDescription>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-green-600">
                {t('payments.success')}
              </CardTitle>
              <CardDescription>
                {t('payments.contactUnlocked')}
              </CardDescription>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-red-600">
                {t('payments.failed')}
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <RefreshCw className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <CardTitle className="text-amber-600">
                {t('payments.pending')}
              </CardTitle>
              <CardDescription>
                {t('payments.pendingDesc')}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent>
          {status === 'success' && contact && (
            <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                {contact.role === 'worker' ? (
                  <User className="h-5 w-5" />
                ) : (
                  <Building2 className="h-5 w-5" />
                )}
                {t('unlock.contactUnlocked')}
              </h3>
              
              <div className="space-y-2">
                <p className="font-medium">{contact.name || contact.company_name}</p>
                
                {contact.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={`mailto:${contact.email}`}
                      className="text-brand hover:underline"
                      data-testid="contact-email"
                    >
                      {contact.email}
                    </a>
                  </div>
                )}
                
                {contact.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={`tel:${contact.phone}`}
                      className="text-brand hover:underline"
                      data-testid="contact-phone"
                    >
                      {contact.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {status === 'success' && !contact && (
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('payments.contactAvailable')}
              </p>
            </div>
          )}
          
          {(status === 'error' || status === 'timeout') && (
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={retryPolling}
                data-testid="retry-payment-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('common.tryAgain')}
              </Button>
              
              {status === 'timeout' && (
                <p className="text-sm text-muted-foreground text-center">
                  {t('payments.checkEmail')}
                </p>
              )}
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col gap-2">
          <Link to="/jobs" className="w-full">
            <Button className="w-full">
              {t('jobs.backToJobs')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          
          <Link to={user.role === 'company' ? '/company/dashboard' : '/worker/dashboard'} className="w-full">
            <Button variant="outline" className="w-full">
              {t('nav.dashboard')}
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

// Payment Cancel Page
export const PaymentCancelPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="payment-cancel-card">
        <CardHeader className="text-center">
          <XCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <CardTitle>{t('payments.cancelled')}</CardTitle>
          <CardDescription>
            {t('payments.cancelledDesc')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground mb-4">
            {t('payments.canTryAgain')}
          </p>
        </CardContent>
        
        <CardFooter className="flex flex-col gap-2">
          {jobId ? (
            <Link to={`/jobs/${jobId}`} className="w-full">
              <Button className="w-full">
                {t('jobs.backToJob')}
              </Button>
            </Link>
          ) : (
            <Link to="/jobs" className="w-full">
              <Button className="w-full">
                {t('jobs.backToJobs')}
              </Button>
            </Link>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
